import appError from '#shared/util/appError.js';
import checkTypes from '#shared/util/checkTypes.js';
import json from '#shared/util/json.js';

/**
 * Open websocket connections by url
 * @type {Map<string, WebSocket>}
 */
const connections = new Map();

/**
 * Helper to check for the expected status of the WebSocket client
 *
 * WebSocket.CONNECTING (0) - Socket has been created. The connection is not yet open.
 * WebSocket.OPEN (1) - The connection is open and ready to communicate.
 * WebSocket.CLOSING (2) - The connection is in the process of closing.
 * WebSocket.CLOSED (3) - The connection is closed or couldn't be opened.
 *
 * @param {WebSocket} ws Existing WebSocket instance
 * @param {number} status Expected status of the provided WebSocket instance
 * @param {number} [timeout=10_000] Reject after given timeout
 * @returns {Promise<void>}
 */
const waitForStatus = (ws, status, timeout = 10_000) => {
	checkTypes('websocket.waitForStatus', {
		ws: [ws, 'object'],
		status: [status, 'number']
	});
	return new Promise((resolve, reject) => {
		const connectionTimeout = setTimeout(() => reject('WebSocket status check timeout'), timeout);
		const checkConnection = () => {
			if (ws instanceof WebSocket && ws.readyState === status) {
				clearTimeout(connectionTimeout);
				resolve();
			} else {
				setTimeout(() => checkConnection(), 500);
			}
		};
		checkConnection();
	});
};

/**
 * Create WebSocket connection on the provided path
 * @param {string} [path='/'] Event request path
 * @returns {Promise<WebSocket>}
 */
const connect = async (path = '/') => {
	checkTypes('websocket.connect', {
		path: [path, 'string']
	});
	const websocketOrigin = location.origin.replace(/^http/, 'ws');
	const { href: wss } = new URL(path, websocketOrigin);
	let ws = connections.get(wss);
	if (!ws || ws instanceof WebSocket && ws.readyState !== WebSocket.OPEN) {
		ws = new WebSocket(wss);
		ws.addEventListener('close', (event) => {
			connections.delete(wss);
			appError('WebSocket connection closed', {
				code: 'APP_FRONTEND_UTIL',
				origin: 'websocket.onClose',
				detail: {
					code: event.code,
					reason: event.reason,
					path: event.target.url
				}
			});
		});
		ws.addEventListener('error', (event) => {
			connections.delete(wss);
			appError('WebSocket connection failed', {
				code: 'APP_FRONTEND_UTIL',
				origin: 'websocket.onError',
				detail: {
					code: event.code,
					reason: event.reason,
					path: event.target.url
				}
			});
		});
	}
	if (ws instanceof WebSocket && ws.readyState === WebSocket.CONNECTING) {
		await waitForStatus(ws, WebSocket.OPEN);
	}
	return connections.set(wss, ws).get(wss);
};

/**
 * Emit message to the server and await response
 * @param {string} path Request message path
 * @param {any} [payload] Request message payload
 * @returns {Promise<any>}
 */
export const emit = (path, payload) => {
	return new Promise((resolve, reject) => {
		connect(path).then((ws) => {
			ws.addEventListener('message', (event) => {
				const { status, data } = json.parse(event.data ?? '{}');
				const resolveOrReject = status === 'ok' ? resolve : reject;
				resolveOrReject(data);
			}, {
				once: true
			});
			ws.send(json.stringify({
				payload
			}));
		}).catch(reject);
	});
};
